/**
 * Margin Report Summary Component
 *
 * Displays key KPIs for the margin report.
 * Calculates percentages dynamically from raw values (same as the table).
 * @see US-25d.13 - Composant MarginReportSummary
 */

import { useMemo } from 'react';
import { KPICard } from '@/components/ui';
import { TrendingUp, TrendingDown, Percent, AlertTriangle } from 'lucide-react';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';
import type { MarginReportResponse } from '@bstock/shared';

interface MarginReportSummaryProps {
  report: MarginReportResponse;
}

/**
 * Calculate percentage with 2 decimal precision (same formula as table)
 */
function calcPercent(value: number, reference: number): number {
  if (reference <= 0) return 0;
  return Math.round((value / reference) * 10000) / 100;
}

/**
 * Format percentage with optional sign
 */
function formatPercent(value: number, showSign = false): string {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

export function MarginReportSummary({ report }: MarginReportSummaryProps) {
  const currency = useCurrency();

  // Reference value for percentages (Ventes Nettes HT)
  const ventesNettes = report.chiffreAffaires.ventesNettesCentimes;

  // Calculate all KPI values from raw data (same logic as table)
  const kpis = useMemo(() => {
    const fcTheorique = report.foodCostNet.theorique;
    const fcReel = report.foodCostNet.reel;
    const ecartTotal = report.ecarts.total;
    const demarque = report.ecarts.nonBulk;

    return {
      fcTheoriquePct: calcPercent(fcTheorique, ventesNettes),
      fcTheoriqueCentimes: fcTheorique,
      fcReelPct: calcPercent(fcReel, ventesNettes),
      fcReelCentimes: fcReel,
      ecartPct: calcPercent(ecartTotal, ventesNettes),
      ecartCentimes: ecartTotal,
      demarquePct: calcPercent(demarque, ventesNettes),
      demarqueCentimes: demarque,
    };
  }, [report, ventesNettes]);

  const isVarianceNegative = kpis.ecartCentimes > 0;
  const isShrinkageNegative = kpis.demarqueCentimes > 0;

  return (
    <div className="grid gap-4 md:grid-cols-4">
      {/* FC Theorique */}
      <KPICard
        title="FC Theorique"
        value={formatPercent(kpis.fcTheoriquePct)}
        subtitle={formatCentimes(kpis.fcTheoriqueCentimes, currency)}
        icon={<Percent className="w-5 h-5" />}
      />

      {/* FC Reel */}
      <KPICard
        title="FC Reel"
        value={formatPercent(kpis.fcReelPct)}
        subtitle={formatCentimes(kpis.fcReelCentimes, currency)}
        icon={<Percent className="w-5 h-5" />}
        badge={
          isVarianceNegative
            ? { label: 'Defavorable', variant: 'danger' }
            : kpis.ecartCentimes < 0
            ? { label: 'Favorable', variant: 'success' }
            : undefined
        }
      />

      {/* Ecart */}
      <KPICard
        title="Ecart"
        value={formatPercent(kpis.ecartPct, true)}
        subtitle={formatCentimes(kpis.ecartCentimes, currency)}
        icon={isVarianceNegative ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
        badge={
          isVarianceNegative
            ? { label: 'Perte', variant: 'danger' }
            : kpis.ecartCentimes < 0
            ? { label: 'Gain', variant: 'success' }
            : undefined
        }
      />

      {/* Demarque Nette */}
      <KPICard
        title="Demarque Nette"
        value={formatPercent(kpis.demarquePct)}
        subtitle={formatCentimes(kpis.demarqueCentimes, currency)}
        icon={<AlertTriangle className="w-5 h-5" />}
        badge={
          isShrinkageNegative
            ? { label: 'A controler', variant: 'warning' }
            : undefined
        }
      />
    </div>
  );
}
